import { useEstabelecimento } from "@/contexts/cadastro/estabelecimento";
import { useServidor } from "@/contexts/cadastro/servidor";
import { AssinaturaRequisicao } from "@/contexts/laboratorio/analiseLaboratorial/components/AssinaturaRequisicao/AssinaturaRequisicao";
import { FormDadosGeraisRequisicao } from "@/contexts/laboratorio/analiseLaboratorial/components/FormDadosGeraisRequisicao/FormDadosGeraisRequisicao";
import { ImpressaoAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/components/ImpressaoAnaliseLaboratorial/ImpressaoAnaliseLaboratorial";
import { useAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/hooks/useAnaliseLaboratorial";
import { AnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/model/AnaliseLaboratorial";
import { RequisicaoAnaliseFormValues } from "@/contexts/laboratorio/analiseLaboratorial/model/RequisicaoAnaliseFormValues";
import { useEnsaiosAnalise } from "@/contexts/laboratorio/ensaioAnalise";
import { useLaboratorioAnalise } from "@/contexts/laboratorio/laboratorioAnalise";
import { useMarcaProduto } from "@/contexts/laboratorio/marcaProduto";
import { useProdutoAutorizado } from "@/contexts/laboratorio/produtoAutorizado";
import { useProgramacaoColetaAnalise } from "@/contexts/laboratorio/programacaoColeta";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import {
  ArrowLeftOutlined,
  FilePdfOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  App,
  Button,
  Col,
  Divider,
  Form,
  Grid,
  Result,
  Row,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useReactToPrint } from "react-to-print";

export function EdicaoRequisicaoAnaliseLaboratorial() {
  const { isOnline } = useNetworkStatus();
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { offlineId } = useParams<{ offlineId: string }>();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm<RequisicaoAnaliseFormValues>();
  const { notification } = App.useApp();
  const requisicaoRef = useRef<HTMLDivElement>(null);
  const { getRequisicaoOfflineById, updateRequisicao, submitRequisicaoApi } =
    useAnaliseLaboratorial();
  const requisicaoOffline = getRequisicaoOfflineById(Number(offlineId));
  const { getEstabelecimentoById } = useEstabelecimento();
  const { getProgramacaoById } = useProgramacaoColetaAnalise();
  const { getProdutoAutorizadoById } = useProdutoAutorizado();
  const { getMarcaProdutoById } = useMarcaProduto();
  const { listaEnsaios } = useEnsaiosAnalise();
  const { getServidorLogged } = useServidor();
  const { getLaboratorioById } = useLaboratorioAnalise();
  const [requisicaoMinuta, setRequisicaoMinuta] =
    useState<AnaliseLaboratorial | null>(null);

  const handleBack = () => {
    navigate(
      `/analises-laboratoriais/requisicoes/lista?${searchParams.toString()}`
    );
  };

  const handleCancel = () => {
    handleBack();
  };

  const handleSubmit = async (values: RequisicaoAnaliseFormValues) => {
    const estabelecimento = getEstabelecimentoById(values.estabelecimento.id);
    const programacao = getProgramacaoById(values.programacao.id);
    const produto = getProdutoAutorizadoById(values.produto.id);
    const marca = getMarcaProdutoById(values.marca.id);
    const laboratorio = getLaboratorioById(values.laboratorio.id);
    const ensaios = listaEnsaios.filter((e) => values.ensaios.includes(e.id));
    const servidor = getServidorLogged();

    if (!estabelecimento || !servidor) {
      notification.error({
        message: "Nao foi possivel salvar a requisicao de analise laboratorial",
        description: "Erro ao buscar dados relacionados",
        key: "submit-requisicao-analise",
      });
      return;
    }

    const request: AnaliseLaboratorial = {
      offlineId: Number(offlineId),
      estabelecimento: estabelecimento!,
      programacao: programacao || undefined,
      produto: produto || undefined,
      marca: marca || undefined,
      agua: values.agua,
      etapaColeta: values.etapaColeta,
      dataColeta: values.dataColeta.format("YYYY-MM-DDTHH:mm:ss"),
      natureza: values.naturezaAnalise,
      condicaoAmostra: values.condicoesAmostra,
      infAdicional: values.informacoesAdicionais,
      justificativa: values.justificativaProgramacao || undefined,
      dataRemessa: values.dataRemessa
        ? values.dataRemessa.format("YYYY-MM-DD")
        : undefined,
      lacre: values.lacre || "",
      lacreCProvaEmp: values.lacreContraProvaEmpresa || "",
      lacreCProvaSise: values.lacreContraProvaSISE || "",
      lote: values.lote || "",
      tamanhoPartida: values.tamanhoPartida || "",
      temperaturaColeta: Number(values.temperaturaAmostra),
      laboratorio: laboratorio || undefined,
      dataValidade: values.dataValidade
        ? values.dataValidade.format("YYYY-MM-DD")
        : undefined,
      dataFabricacao: values.dataFabricacao
        ? values.dataFabricacao.format("YYYY-MM-DD")
        : undefined,
      observacaoColeta: values.observacao || "",
      statusAssinaturaFiscalizado:
        values.statusAssinatura || "ASSINADO_FISICAMENTE",
      assinaturaFiscalizado: values.assinaturaFiscalizado || "",
      assinaturaServidor: values.assinaturaServidor || "",
      cpfAssinanteFiscalizado: values.cpfAssinante?.replace(/\D/g, "") || "",
      funcaoAssinanteFiscalizado: values.funcaoAssinante || "",
      nomeAssinanteFiscalizado: values.nomeAssinante || "",
      itens: ensaios.map((ensaio) => ({
        ensaio,
        resultado: "",
        conforme: false,
        naoRealizada: false,
      })),
      responsavelColeta: {
        id: servidor.id,
        matricula: servidor.matricula,
        cpfCnpj: servidor.pessoa.cpfCnpj,
        inscricaoEstadual: servidor.pessoa.inscricaoEstadual,
        nome: servidor.pessoa.nome,
        funcao: servidor.funcao,
      },
      fiscal: values.analiseFiscal || false,
      nomeEstabelecimento: estabelecimento!.nome || "",
      nomeMarca: marca?.nome || "",
      statusAssinaturaServidor:
        values.statusAssinatura === "ASSINADO_FISICAMENTE"
          ? "ASSINADO_FISICAMENTE"
          : "ASSINADO_DIGITALMENTE",
    };

    if (!isOnline) {
      //OFFLINE
      updateRequisicao(request);
      notification.success({
        message: "Requisição de Análise Laboratorial salva com sucesso",
        description:
          "As alterações da requisição foram salvas localmente e ficará com status de pendente até ser sincronizada com o servidor. Armazene o arquivo de backup para evitar perda dos dados.",
      });

      saveAs(
        new Blob([JSON.stringify(request)], {
          type: "text/plain;charset=utf-8",
        }),
        "backup_requisicao_analise_laboratorial-" + Date.now() + ".txt"
      );

      handleBack();
      return;
    }

    try {
      //ONLINE
      const result = await submitRequisicaoApi(request);
      notification.success({
        message: "Requisição de Análise Laboratorial salva com sucesso",
        description: "A requisição foi sincronizada com o servidor.",
      });

      updateRequisicao({
        ...request,
        id: result.id,
      });
      handleBack();
    } catch (error) {
      console.error(error);
      const messageError = formatMessageError(error);
      notification.error({
        message: "Nao foi possivel salvar a requisição de analise laboratorial",
        description: messageError,
        key: "submit-requisicao-analise",
      });
      return;
    }
  };

  const handleGeneratePreview = async () => {
    try {
      const values = await form.validateFields({
        validateOnly: true,
      });

      const estabelecimento = getEstabelecimentoById(values.estabelecimento.id);
      const programacao = getProgramacaoById(values.programacao.id);
      const produto = getProdutoAutorizadoById(values.produto.id);
      const marca = getMarcaProdutoById(values.marca.id);
      const laboratorio = getLaboratorioById(values.laboratorio.id);
      const ensaios = listaEnsaios.filter((e) => values.ensaios.includes(e.id));
      const servidor = getServidorLogged();

      if (!estabelecimento || !servidor) {
        notification.error({
          message:
            "Nao foi possivel Gerar a prévia da requisição de analise laboratorial",
          description: "Erro ao buscar dados relacionados",
          key: "preview-requisicao-analise",
        });
        return;
      }

      const request: AnaliseLaboratorial = {
        estabelecimento: estabelecimento!,
        programacao: programacao || undefined,
        produto: produto || undefined,
        marca: marca || undefined,
        agua: values.agua,
        etapaColeta: values.etapaColeta,
        dataColeta: values.dataColeta.format("YYYY-MM-DDTHH:mm:ss"),
        natureza: values.naturezaAnalise,
        condicaoAmostra: values.condicoesAmostra,
        infAdicional: values.informacoesAdicionais,
        justificativa: values.justificativaProgramacao || undefined,
        dataRemessa: values.dataRemessa.format("YYYY-MM-DD"),
        lacre: values.lacre || "",
        lacreCProvaEmp: values.lacreContraProvaEmpresa || "",
        lacreCProvaSise: values.lacreContraProvaSISE || "",
        lote: values.lote || "",
        tamanhoPartida: values.tamanhoPartida || "",
        temperaturaColeta: Number(values.temperaturaAmostra),
        laboratorio: laboratorio || undefined,
        dataValidade: values.dataValidade
          ? values.dataValidade.format("YYYY-MM-DD")
          : "",
        dataFabricacao: values.dataFabricacao
          ? values.dataFabricacao.format("YYYY-MM-DD")
          : "",
        observacaoColeta: values.observacao || "",
        statusAssinaturaFiscalizado:
          values.statusAssinatura || "ASSINADO_FISICAMENTE",
        assinaturaFiscalizado: values.assinaturaFiscalizado || "",
        assinaturaServidor: values.assinaturaServidor || "",
        cpfAssinanteFiscalizado: values.cpfAssinante?.replace(/\D/g, "") || "",
        funcaoAssinanteFiscalizado: values.funcaoAssinante || "",
        nomeAssinanteFiscalizado: values.nomeAssinante || "",
        itens: ensaios.map((ensaio) => ({
          ensaio,
          resultado: "",
          conforme: false,
          naoRealizada: false,
        })),
        responsavelColeta: {
          id: servidor.id,
          matricula: servidor.matricula,
          cpfCnpj: servidor.pessoa.cpfCnpj,
          inscricaoEstadual: servidor.pessoa.inscricaoEstadual,
          nome: servidor.pessoa.nome,
          funcao: servidor.funcao,
        },
        fiscal: values.analiseFiscal || false,
        nomeEstabelecimento: estabelecimento!.nome || "",
        nomeMarca: marca?.nome || "",
        statusAssinaturaServidor:
          values.statusAssinatura === "ASSINADO_FISICAMENTE"
            ? "ASSINADO_FISICAMENTE"
            : "ASSINADO_DIGITALMENTE",
      };

      console.log("preview", request);

      setRequisicaoMinuta(request);
    } catch (error) {
      console.error(error);
      notification.error({
        message: "Verifique os campos obrigatórios",
        key: "preview-requisicao-analise",
      });
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: requisicaoRef,
    documentTitle: "requisicao_analise_laboratorial_minuta",
    suppressErrors: true,
    preserveAfterPrint: true,
    onAfterPrint: () => setRequisicaoMinuta(null),
  });

  useEffect(() => {
    if (requisicaoMinuta) {
      handlePrint();
    }
  }, [requisicaoMinuta, handlePrint]);

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Edição Requisição de Análise Laboratorial";
  }, []);

  if (!requisicaoOffline) {
    return (
      <Result
        status="404"
        title="Requisição de Análise Laboratorial não encontrada"
      />
    );
  }

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Edição Requisição de Análise Laboratorial
        </Typography.Title>
      </Space>

      <Divider />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={
          {
            estabelecimento: {
              id: requisicaoOffline.estabelecimento?.id,
            },
            programacao: {
              id: requisicaoOffline.programacao?.id,
            },
            produto: {
              id: requisicaoOffline.produto?.id,
            },
            marca: {
              id: requisicaoOffline.marca?.id,
            },
            agua: requisicaoOffline.agua,
            etapaColeta: requisicaoOffline.etapaColeta,
            dataColeta: dayjs(requisicaoOffline.dataColeta),
            condicoesAmostra: requisicaoOffline.condicaoAmostra,
            analiseFiscal: requisicaoOffline.fiscal || false,
            naturezaAnalise: requisicaoOffline.natureza,
            informacoesAdicionais: requisicaoOffline.infAdicional,
            dataValidade:
              requisicaoOffline.dataValidade &&
              dayjs(requisicaoOffline.dataValidade),
            dataFabricacao:
              requisicaoOffline.dataFabricacao &&
              dayjs(requisicaoOffline.dataFabricacao),
            dataRemessa:
              requisicaoOffline.dataRemessa &&
              dayjs(requisicaoOffline.dataRemessa),
            lacre: requisicaoOffline.lacre,
            lacreContraProvaEmpresa: requisicaoOffline.lacreCProvaEmp,
            lote: requisicaoOffline.lote,
            lacreContraProvaSISE: requisicaoOffline.lacreCProvaSise,
            tamanhoPartida: requisicaoOffline.tamanhoPartida,
            temperaturaAmostra: requisicaoOffline.temperaturaColeta || "",
            laboratorio: {
              id: requisicaoOffline.laboratorio?.id,
            },
            observacao: requisicaoOffline.observacaoColeta,
            ensaios: requisicaoOffline.itens.flatMap((i) =>
              i.ensaio ? [i.ensaio.id] : []
            ),
          } as RequisicaoAnaliseFormValues
        }
        onFinishFailed={(errorInfo) => {
          console.log("Failed:", errorInfo);

          notification.error({
            message: "Verifique os campos obrigatórios",
            description: errorInfo.errorFields
              .map((err) => err.name)
              .join(", "),
            key: "form-error",
            duration: 5,
          });
        }}
      >
        <FormDadosGeraisRequisicao form={form} isEdit />

        <Divider orientation="left">
          <Typography.Title level={screens.xs ? 5 : 4}>
            Assinaturas
          </Typography.Title>
        </Divider>

        <AssinaturaRequisicao form={form} />

        <Divider />

        <Row justify={"center"} gutter={[10, 10]}>
          <Col>
            <Button
              htmlType="button"
              icon={<ArrowLeftOutlined />}
              onClick={handleCancel}
              style={{ width: 200 }}
            >
              Voltar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<SaveOutlined />}
              type="primary"
              htmlType="submit"
              style={{ width: 200 }}
            >
              Salvar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<FilePdfOutlined />}
              htmlType="button"
              style={{ width: 200 }}
              onClick={handleGeneratePreview}
            >
              Gerar Prévia
            </Button>
          </Col>
        </Row>
      </Form>

      <div style={{ display: "none" }}>
        <ImpressaoAnaliseLaboratorial
          marcadagua="MINUTA"
          ref={requisicaoRef}
          item={requisicaoMinuta}
        />
      </div>
    </>
  );
}
